import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Save, Plus, Trash2, Clock, RotateCcw, History, Edit3, X as XIcon, ListChecks } from 'lucide-react';
import { toast } from 'react-toastify';
import {
  useClinicSettingsQuery,
  useUpdateClinicSettingsMutation,
  useAddHolidayMutation,
  useRemoveHolidayMutation,
} from '../../api/settingsApi';
import {
  useActivityQuery,
  useDeletedLeadsQuery, useRestoreLeadMutation,
  useDeletedPatientsQuery, useRestorePatientMutation,
} from '../../api/platformApi';
import {
  useListProcedureCatalogQuery,
  useCreateProcedureCatalogItemMutation,
  useUpdateProcedureCatalogItemMutation,
  useDeleteProcedureCatalogItemMutation,
} from '../../api/procedureCatalogApi';
import { selectHasRole } from '../auth/authSlice';
import { apiErrorMessage } from '../../utils/apiError';

const DAYS = [
  { n: 0, label: 'Sun' }, { n: 1, label: 'Mon' }, { n: 2, label: 'Tue' }, { n: 3, label: 'Wed' },
  { n: 4, label: 'Thu' }, { n: 5, label: 'Fri' }, { n: 6, label: 'Sat' },
];

// TimeOnly serializes as "HH:mm:ss"; the <input type=time> wants "HH:mm".
const toInput = (t) => (t ? String(t).slice(0, 5) : '');
const toApi = (v) => (v ? `${v}:00` : null);

export default function SettingsPage() {
  const { data, isLoading } = useClinicSettingsQuery();
  const [updateSettings, { isLoading: saving }] = useUpdateClinicSettingsMutation();
  const [addHoliday] = useAddHolidayMutation();
  const [removeHoliday] = useRemoveHolidayMutation();

  const isAdmin = useSelector(selectHasRole('Admin'));
  const [form, setForm] = useState(null);
  const [holidayDate, setHolidayDate] = useState('');
  const [holidayName, setHolidayName] = useState('');

  useEffect(() => {
    if (data) {
      setForm({
        openingTime: toInput(data.openingTime) || '09:00',
        closingTime: toInput(data.closingTime) || '20:00',
        hasBreak: !!(data.breakStart && data.breakEnd),
        breakStart: toInput(data.breakStart) || '13:00',
        breakEnd: toInput(data.breakEnd) || '14:00',
        weeklyOffDays: data.weeklyOffDays ?? [],
        slotMinutes: data.slotMinutes ?? 30,
        allowDoubleBooking: !!data.allowDoubleBooking,
        timeZoneId: data.timeZoneId ?? '',
      });
    }
  }, [data]);

  if (isLoading || !form) {
    return (
      <>
        <Header />
        <div className="skeleton" style={{ height: 24, maxWidth: 320, marginTop: 16 }} />
      </>
    );
  }

  const toggleDay = (n) =>
    setForm((f) => ({
      ...f,
      weeklyOffDays: f.weeklyOffDays.includes(n) ? f.weeklyOffDays.filter((d) => d !== n) : [...f.weeklyOffDays, n],
    }));

  const save = async () => {
    try {
      await updateSettings({
        openingTime: toApi(form.openingTime),
        closingTime: toApi(form.closingTime),
        breakStart: form.hasBreak ? toApi(form.breakStart) : null,
        breakEnd: form.hasBreak ? toApi(form.breakEnd) : null,
        weeklyOffDays: form.weeklyOffDays,
        slotMinutes: Number(form.slotMinutes) || 30,
        allowDoubleBooking: form.allowDoubleBooking,
        timeZoneId: form.timeZoneId.trim() || null,
      }).unwrap();
      toast.success('Clinic settings saved');
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to save settings'));
    }
  };

  const submitHoliday = async (e) => {
    e.preventDefault();
    if (!holidayDate) return;
    try {
      await addHoliday({ date: holidayDate, name: holidayName.trim() || null }).unwrap();
      setHolidayDate(''); setHolidayName('');
      toast.success('Holiday added');
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Failed to add holiday'));
    }
  };

  const deleteHoliday = async (id) => {
    try { await removeHoliday(id).unwrap(); toast.success('Holiday removed'); }
    catch (err) { toast.error(apiErrorMessage(err, 'Failed to remove holiday')); }
  };

  return (
    <>
      <Header />

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header"><h3><Clock size={15} style={{ verticalAlign: '-2px', marginRight: 6 }} />Working hours</h3></div>
        <div className="card-body">
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Opening time</label>
              <input className="form-control" type="time" value={form.openingTime} onChange={(e) => setForm({ ...form, openingTime: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">Closing time</label>
              <input className="form-control" type="time" value={form.closingTime} onChange={(e) => setForm({ ...form, closingTime: e.target.value })} />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={form.hasBreak} onChange={(e) => setForm({ ...form, hasBreak: e.target.checked })} />
              Daily break
            </label>
          </div>
          {form.hasBreak && (
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Break start</label>
                <input className="form-control" type="time" value={form.breakStart} onChange={(e) => setForm({ ...form, breakStart: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Break end</label>
                <input className="form-control" type="time" value={form.breakEnd} onChange={(e) => setForm({ ...form, breakEnd: e.target.value })} />
              </div>
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Weekly off days</label>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {DAYS.map((d) => (
                <button
                  type="button"
                  key={d.n}
                  onClick={() => toggleDay(d.n)}
                  className={`btn btn-sm ${form.weeklyOffDays.includes(d.n) ? 'btn-danger' : 'btn-outline'}`}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Slot length (min)</label>
              <input className="form-control" type="number" min="5" step="5" value={form.slotMinutes} onChange={(e) => setForm({ ...form, slotMinutes: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">Timezone ID (optional)</label>
              <input className="form-control" placeholder="e.g. India Standard Time" value={form.timeZoneId} onChange={(e) => setForm({ ...form, timeZoneId: e.target.value })} />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={form.allowDoubleBooking} onChange={(e) => setForm({ ...form, allowDoubleBooking: e.target.checked })} />
              Allow double-booking (same doctor, overlapping appointments)
            </label>
          </div>

          <button className="btn btn-primary" onClick={save} disabled={saving || !isAdmin} title={isAdmin ? '' : 'Only admins can change clinic settings'}>
            <Save size={16} /> {saving ? 'Saving…' : 'Save settings'}
          </button>
          {!isAdmin && <span className="td-muted" style={{ marginLeft: 10, fontSize: '0.8rem' }}>Read-only — admin access required to edit.</span>}
        </div>
      </div>

      <div className="card">
        <div className="card-header"><h3>Holidays &amp; special closures</h3></div>
        <div className="card-body">
          <form onSubmit={submitHoliday} className="form-row" style={{ alignItems: 'flex-end' }}>
            <div className="form-group">
              <label className="form-label">Date</label>
              <input className="form-control" type="date" value={holidayDate} onChange={(e) => setHolidayDate(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Name (optional)</label>
              <input className="form-control" placeholder="e.g. Diwali" value={holidayName} onChange={(e) => setHolidayName(e.target.value)} />
            </div>
            <div className="form-group" style={{ flex: '0 0 auto' }}>
              <button type="submit" className="btn btn-outline"><Plus size={16} /> Add</button>
            </div>
          </form>

          <div className="tbl-wrap" style={{ marginTop: 8 }}>
            <table>
              <thead><tr><th>Date</th><th>Name</th><th style={{ textAlign: 'right' }}>Actions</th></tr></thead>
              <tbody>
                {(data.holidays ?? []).map((h) => (
                  <tr key={h.id}>
                    <td>{new Date(h.date).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric' })}</td>
                    <td className="td-muted">{h.name || '—'}</td>
                    <td style={{ textAlign: 'right' }}>
                      <button className="btn btn-sm btn-outline" onClick={() => deleteHoliday(h.id)}><Trash2 size={14} /></button>
                    </td>
                  </tr>
                ))}
                {(data.holidays ?? []).length === 0 && (
                  <tr><td colSpan={3} className="td-muted" style={{ textAlign: 'center', padding: 18 }}>No holidays configured.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <ProcedureCatalogCard isAdmin={isAdmin} />

      {isAdmin && <RecycleBinCard />}
      <ActivityLogCard />
    </>
  );
}

// Admin-managed master list of billable procedures + standard rate. Used to seed
// (not lock) the description/rate fields when creating a billing line item.
function ProcedureCatalogCard({ isAdmin }) {
  const { data: items = [], isLoading } = useListProcedureCatalogQuery(false);
  const [createItem, { isLoading: creating }] = useCreateProcedureCatalogItemMutation();
  const [updateItem] = useUpdateProcedureCatalogItemMutation();
  const [deleteItem] = useDeleteProcedureCatalogItemMutation();

  const [name, setName] = useState('');
  const [baseCost, setBaseCost] = useState('');
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editCost, setEditCost] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      await createItem({ name: name.trim(), baseCost: Number(baseCost) || 0 }).unwrap();
      setName(''); setBaseCost('');
      toast.success('Procedure added');
    } catch (err) { toast.error(apiErrorMessage(err, 'Failed to add procedure')); }
  };

  const beginEdit = (p) => { setEditId(p.id); setEditName(p.name); setEditCost(String(p.baseCost)); };
  const cancelEdit = () => { setEditId(null); setEditName(''); setEditCost(''); };
  const saveEdit = async () => {
    if (!editName.trim()) return;
    try {
      await updateItem({ id: editId, name: editName.trim(), baseCost: Number(editCost) || 0 }).unwrap();
      cancelEdit();
      toast.success('Procedure updated');
    } catch (err) { toast.error(apiErrorMessage(err, 'Failed to update procedure')); }
  };

  const toggleActive = async (p) => {
    try { await updateItem({ id: p.id, isActive: !p.isActive }).unwrap(); }
    catch (err) { toast.error(apiErrorMessage(err, 'Failed to update procedure')); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}" from the procedure catalog?`)) return;
    try { await deleteItem(p.id).unwrap(); toast.success('Procedure removed'); }
    catch (err) { toast.error(apiErrorMessage(err, 'Failed to delete procedure')); }
  };

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-header"><h3><ListChecks size={15} style={{ verticalAlign: '-2px', marginRight: 6 }} />Procedure &amp; Pricing Management</h3></div>
      <div className="card-body">
        {isAdmin && (
          <form onSubmit={submit} className="form-row" style={{ alignItems: 'flex-end', marginBottom: 12 }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Procedure / Treatment name</label>
              <input className="form-control" placeholder="e.g. Physiotherapy Session" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="form-group" style={{ flex: '0 0 160px' }}>
              <label className="form-label">Base cost (₹)</label>
              <input className="form-control" type="number" min="0" step="any" value={baseCost} onChange={(e) => setBaseCost(e.target.value)} />
            </div>
            <div className="form-group" style={{ flex: '0 0 auto' }}>
              <button type="submit" className="btn btn-outline" disabled={creating || !name.trim()}>
                <Plus size={16} /> Add
              </button>
            </div>
          </form>
        )}

        <div className="tbl-wrap">
          <table>
            <thead><tr><th>Name</th><th style={{ textAlign: 'right' }}>Base cost</th><th>Status</th><th style={{ textAlign: 'right' }}>Actions</th></tr></thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={4} style={{ textAlign: 'center', padding: 18 }}>
                  <div className="skeleton" style={{ height: 18, maxWidth: 260, margin: '0 auto' }} />
                </td></tr>
              )}
              {items.map((p) => {
                const isEd = editId === p.id;
                return (
                  <tr key={p.id}>
                    <td className="td-bold">
                      {isEd ? (
                        <input className="form-control" value={editName} onChange={(e) => setEditName(e.target.value)} autoFocus />
                      ) : p.name}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {isEd ? (
                        <input className="form-control" type="number" min="0" step="any" style={{ textAlign: 'right' }}
                          value={editCost} onChange={(e) => setEditCost(e.target.value)} />
                      ) : new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(p.baseCost)}
                    </td>
                    <td>
                      {isAdmin ? (
                        <button className={`badge ${p.isActive ? 'b-green' : 'b-gray'}`} style={{ border: 'none', cursor: 'pointer' }} onClick={() => toggleActive(p)}>
                          {p.isActive ? 'Active' : 'Inactive'}
                        </button>
                      ) : (
                        <span className={`badge ${p.isActive ? 'b-green' : 'b-gray'}`}>{p.isActive ? 'Active' : 'Inactive'}</span>
                      )}
                    </td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {isAdmin && (
                        isEd ? (
                          <>
                            <button className="btn btn-sm btn-primary" onClick={saveEdit}><Save size={13} /></button>{' '}
                            <button className="btn btn-sm btn-outline" onClick={cancelEdit}><XIcon size={13} /></button>
                          </>
                        ) : (
                          <>
                            <button className="btn-icon-sm" title="Edit" onClick={() => beginEdit(p)}><Edit3 size={14} /></button>
                            <button className="btn-icon-sm" title="Delete" onClick={() => remove(p)}><Trash2 size={14} color="var(--red-500)" /></button>
                          </>
                        )
                      )}
                    </td>
                  </tr>
                );
              })}
              {!isLoading && items.length === 0 && (
                <tr><td colSpan={4} className="td-muted" style={{ textAlign: 'center', padding: 18 }}>No procedures in the catalog yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {!isAdmin && <span className="td-muted" style={{ fontSize: '0.8rem' }}>Read-only — admin access required to manage the catalog.</span>}
      </div>
    </div>
  );
}

function RecycleBinCard() {
  const { data: leads = [] } = useDeletedLeadsQuery();
  const { data: patients = [] } = useDeletedPatientsQuery();
  const [restoreLead] = useRestoreLeadMutation();
  const [restorePatient] = useRestorePatientMutation();

  const doRestoreLead = async (id) => {
    try { await restoreLead(id).unwrap(); toast.success('Lead restored'); }
    catch (e) { toast.error(apiErrorMessage(e, 'Failed to restore')); }
  };
  const doRestorePatient = async (id) => {
    try { await restorePatient(id).unwrap(); toast.success('Patient restored'); }
    catch (e) { toast.error(apiErrorMessage(e, 'Failed to restore')); }
  };

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="card-header"><h3><Trash2 size={15} style={{ verticalAlign: '-2px', marginRight: 6 }} />Recycle bin</h3></div>
      <div className="card-body">
        <h4 style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', margin: '0 0 6px' }}>Deleted leads</h4>
        <div className="tbl-wrap" style={{ marginBottom: 16 }}>
          <table>
            <thead><tr><th>Name</th><th>Phone</th><th style={{ textAlign: 'right' }}>Action</th></tr></thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.id}>
                  <td className="td-bold">{l.fullName}</td>
                  <td className="td-mono">{l.phoneNumber}</td>
                  <td style={{ textAlign: 'right' }}>
                    <button className="btn btn-sm btn-outline" onClick={() => doRestoreLead(l.id)}><RotateCcw size={14} /> Restore</button>
                  </td>
                </tr>
              ))}
              {leads.length === 0 && <tr><td colSpan={3} className="td-muted" style={{ textAlign: 'center', padding: 14 }}>No deleted leads.</td></tr>}
            </tbody>
          </table>
        </div>

        <h4 style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', margin: '0 0 6px' }}>Deleted patients</h4>
        <div className="tbl-wrap">
          <table>
            <thead><tr><th>Code</th><th>Name</th><th style={{ textAlign: 'right' }}>Action</th></tr></thead>
            <tbody>
              {patients.map((p) => (
                <tr key={p.id}>
                  <td className="td-mono">{p.patientCode}</td>
                  <td className="td-bold">{p.fullName}</td>
                  <td style={{ textAlign: 'right' }}>
                    <button className="btn btn-sm btn-outline" onClick={() => doRestorePatient(p.id)}><RotateCcw size={14} /> Restore</button>
                  </td>
                </tr>
              ))}
              {patients.length === 0 && <tr><td colSpan={3} className="td-muted" style={{ textAlign: 'center', padding: 14 }}>No deleted patients.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ActivityLogCard() {
  const { data } = useActivityQuery({ page: 1, pageSize: 25 });
  const rows = data?.items ?? [];
  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="card-header"><h3><History size={15} style={{ verticalAlign: '-2px', marginRight: 6 }} />Recent activity</h3></div>
      <div className="tbl-wrap" style={{ border: 'none' }}>
        <table>
          <thead><tr><th>When</th><th>Action</th><th>Details</th><th>By</th></tr></thead>
          <tbody>
            {rows.map((a) => (
              <tr key={a.id}>
                <td className="td-muted">{new Date(a.occurredAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</td>
                <td><span className="badge b-gray">{a.entityType} · {a.action}</span></td>
                <td>{a.summary || '—'}</td>
                <td className="td-muted">{a.actorName || 'System'}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={4} className="td-muted" style={{ textAlign: 'center', padding: 18 }}>No activity recorded yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Header() {
  return (
    <div className="page-header">
      <div className="breadcrumb"><span>Home</span><span className="bc-sep">/</span><span className="bc-cur">Settings</span></div>
      <div className="page-header-row">
        <div>
          <h1>Clinic Settings</h1>
          <div className="page-header-sub">Working hours, breaks, weekly offs, holidays and booking rules.</div>
        </div>
      </div>
    </div>
  );
}
